import { CacheProvider } from '@emotion/react';
import React, { Suspense, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { CssBaseline } from '@mui/material';
import { AppLayout } from 'layouts';
import { IndexPage, NoMatch } from 'pages';
import { RoomPage } from 'features/room';
import { useAppSelector } from 'store/hooks';
import { selectBrandingOptions } from 'features/branding/brandingSlice';
import { createTheme } from 'theme';
import { useWindowHeight } from 'hooks/useWindowHeight';
import ErrorBoundaryFallback from 'components/ErrorBoundaryFallback';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import 'theme/common.css';
import 'theme/icons.css';

import 'react-resizable/css/styles.css';
import 'features/notifications/toast/toasts.css';
import { emotionCache } from 'utils/css';

const renderRoute = () => {
  const segments = window.location.pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return <IndexPage />;
  }

  if (segments.length === 1) {
    return <RoomPage />;
  }

  return <NoMatch />;
};

function App() {
  useWindowHeight();

  const brandingOptions = useAppSelector(selectBrandingOptions);

  const theme = useMemo(() => createTheme(brandingOptions), [brandingOptions]);

  return (
    <ThemeProvider theme={theme}>
      <CacheProvider value={emotionCache}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />}>
            <Suspense fallback={null}>
              <AppLayout>{renderRoute()}</AppLayout>
            </Suspense>
          </Sentry.ErrorBoundary>
        </LocalizationProvider>
      </CacheProvider>
    </ThemeProvider>
  );
}

export default App;
